import { useCallback, useEffect, useRef, useState } from 'react';
import { getAIChatHistoryApi, postAIChatMessageApi } from '@/api/aiChatApi';
import { store } from '@/config/store';
import {
  AIChatMessage,
  AISession,
  AIWebSocketMessage,
} from '@/types/api/aiChat';
import { getAiLoginType } from '@/utils/aiLoginType';

/** 履歴の1回あたり取得件数（1往復=2件なので15往復ぶん） */
const HISTORY_PAGE_SIZE = 30;

interface UseAIChatResult {
  messages: AIChatMessage[];
  isLoading: boolean;
  isSending: boolean;
  isStreaming: boolean;
  streamingContent: string;
  error: string | null;
  hasMoreHistory: boolean;
  isLoadingOlder: boolean;
  fetchHistory: () => Promise<void>;
  loadOlderMessages: () => Promise<void>;
  sendMessage: (message: string) => Promise<void>;
  connectWebSocket: () => void;
  disconnectWebSocket: () => void;
}

/**
 * AIチャットフック
 * メッセージの送受信、履歴取得、WebSocketストリーミング対応
 */
export const useAIChat = (session: AISession | null): UseAIChatResult => {
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  /** 楽観表示した仮メッセージのID。WebSocketの応答で差し替えるために覚えておく */
  const pendingTempIdRef = useRef<number | null>(null);
  /** 古い履歴の取得中フラグ。連続するスクロールイベントで多重実行しないため */
  const loadingOlderRef = useRef(false);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // 対話履歴を取得（最新側から1ページぶん）
  const fetchHistory = useCallback(async () => {
    if (!session) return;

    setIsLoading(true);
    setError(null);

    try {
      const res = await getAIChatHistoryApi({ limit: HISTORY_PAGE_SIZE });

      if (res.data.success && res.data.data) {
        setMessages(res.data.data.messages);
        setHasMoreHistory(res.data.data.has_more);
      } else {
        throw new Error(res.data.error_message || '履歴取得に失敗しました');
      }
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : '履歴取得中にエラーが発生しました';
      setError(errorMessage);
      console.error('AI履歴取得エラー:', e);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  // 画面を上に遡ったときに、より古い履歴を継ぎ足す
  const loadOlderMessages = useCallback(async () => {
    // 多重実行の防止は state ではなく ref で見る。
    // スクロールイベントは連続して発火し、setIsLoadingOlder が反映される前に
    // 次の呼び出しが来るため、state だと同じ範囲を二重に読んでしまう
    if (!session || loadingOlderRef.current || !hasMoreHistory) return;

    const oldest = messages[0];

    if (!oldest) return;

    loadingOlderRef.current = true;
    setIsLoadingOlder(true);

    try {
      const res = await getAIChatHistoryApi({
        limit: HISTORY_PAGE_SIZE,
        before_id: oldest.id,
      });

      if (res.data.success && res.data.data) {
        const older = res.data.data.messages;

        // 念のため既にあるものは弾く（重複すると表示位置の計算も狂う）
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));

          return [...older.filter((m) => !existingIds.has(m.id)), ...prev];
        });
        setHasMoreHistory(res.data.data.has_more);
      }
    } catch (e) {
      console.error('AI履歴の追加取得エラー:', e);
    } finally {
      loadingOlderRef.current = false;
      setIsLoadingOlder(false);
    }
  }, [session, messages, hasMoreHistory]);

  // WebSocket接続
  const connectWebSocket = useCallback(() => {
    if (!session || wsRef.current?.readyState === WebSocket.OPEN) return;

    const authInfo = store.state.authInfo as { token?: string };
    if (!authInfo?.token) return;

    // WebSocket URLの構築（API Gateway WebSocket）
    const wsBaseUrl = import.meta.env.VITE_AI_WEBSOCKET_URL;
    if (!wsBaseUrl) {
      console.warn('WebSocket URL未設定、HTTP fallbackを使用');
      return;
    }

    const wsUrl = `${wsBaseUrl}?token=${encodeURIComponent(
      authInfo.token,
    )}&session_id=${session.id}`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('AI WebSocket接続成功');
      };

      ws.onmessage = (event) => {
        try {
          const data: AIWebSocketMessage = JSON.parse(event.data);

          switch (data.type) {
            case 'chunk':
              setIsStreaming(true);
              setStreamingContent((prev) => prev + (data.content || ''));
              break;

            case 'complete':
              setIsStreaming(false);
              // 楽観表示した仮メッセージを、保存済みレコードに置き換える。
              // 履歴を取り直すと遡って読んだぶんが消えるのでここでは再取得しない
              if (data.data) {
                const {
                  user_message: userMessage,
                  assistant_message: assistantMessage,
                } = data.data;

                setMessages((prev) => [
                  ...prev.filter((m) => m.id !== pendingTempIdRef.current),
                  userMessage,
                  assistantMessage,
                ]);
                pendingTempIdRef.current = null;
              }
              setStreamingContent('');
              setIsSending(false);
              break;

            case 'error':
              setError(data.error_message || 'エラーが発生しました');
              setIsStreaming(false);
              setStreamingContent('');
              setIsSending(false);
              break;

            case 'escalation':
              // エスカレーション通知
              setIsStreaming(false);
              fetchHistory();
              setStreamingContent('');
              setIsSending(false);
              break;
          }
        } catch (e) {
          console.error('WebSocketメッセージ解析エラー:', e);
        }
      };

      ws.onerror = (event) => {
        console.error('AI WebSocketエラー:', event);
        setError('接続エラーが発生しました');
      };

      ws.onclose = () => {
        console.log('AI WebSocket切断');
        // 再接続を試みる
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          if (session) {
            connectWebSocket();
          }
        }, 3000);
      };

      wsRef.current = ws;
    } catch (e) {
      console.error('WebSocket接続エラー:', e);
    }
  }, [session, fetchHistory]);

  // WebSocket切断
  const disconnectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  // メッセージ送信
  const sendMessage = useCallback(
    async (message: string) => {
      if (!session || isSending) return;

      const authInfo = store.state.authInfo as {
        m_customer_id: number;
        token?: string;
      };
      if (!authInfo?.m_customer_id) return;

      setIsSending(true);
      setError(null);

      // ユーザーメッセージを即座に表示（楽観的更新）
      const tempUserMessage: AIChatMessage = {
        id: Date.now(),
        session_id: session.id,
        m_customer_id: authInfo.m_customer_id,
        role: 'user',
        message,
        is_escalated: false,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, tempUserMessage]);
      pendingTempIdRef.current = tempUserMessage.id;

      // WebSocketが接続されている場合はWebSocket経由で送信
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          // 送信者はサーバがトークンから判定する。m_customer_id は送らない。
          // login_type は「AIが参照する顧客レコードの範囲」を車両切替の
          // 表示範囲と揃えるためにサーバが使う（識別子の値は送らない）
          JSON.stringify({
            action: 'chat',
            message,
            token: authInfo.token,
            login_type: getAiLoginType(),
          }),
        );
        return;
      }

      // HTTP fallback
      try {
        const res = await postAIChatMessageApi({ message });

        if (res.data.success && res.data.data) {
          // 楽観的に追加したメッセージを正式なものに置き換え
          setMessages((prev) => {
            const filtered = prev.filter((m) => m.id !== tempUserMessage.id);
            return [
              ...filtered,
              res.data.data!.user_message,
              res.data.data!.assistant_message,
            ];
          });
        } else {
          throw new Error(
            res.data.error_message || 'メッセージ送信に失敗しました',
          );
        }
      } catch (e) {
        const errorMessage =
          e instanceof Error
            ? e.message
            : 'メッセージ送信中にエラーが発生しました';
        setError(errorMessage);
        console.error('AIメッセージ送信エラー:', e);
        // 失敗した場合は楽観的に追加したメッセージを削除
        setMessages((prev) => prev.filter((m) => m.id !== tempUserMessage.id));
      } finally {
        setIsSending(false);
      }
    },
    [session, isSending],
  );

  // セッション変更時に履歴を取得
  useEffect(() => {
    if (session) {
      fetchHistory();
    }
  }, [session, fetchHistory]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      disconnectWebSocket();
    };
  }, [disconnectWebSocket]);

  return {
    messages,
    isLoading,
    isSending,
    isStreaming,
    streamingContent,
    error,
    hasMoreHistory,
    isLoadingOlder,
    fetchHistory,
    loadOlderMessages,
    sendMessage,
    connectWebSocket,
    disconnectWebSocket,
  };
};
